import { bindingMode } from 'aurelia-binding';
import {
	autoinject,
	bindable,
	customElement,
	newInstance,
} from 'aurelia-framework';
import { ValidationController } from 'aurelia-validation';
import PerfectScrollbar from 'perfect-scrollbar';

import { BaseViewModel } from '~/base-view-model';
import { Logger } from '~/utils/logger';

enum SlideoutType {
	DEFAULT = 'default',
	FORM = 'form',
	FILTER = 'filter',
}

enum SlideoutEvents {
	EDIT,
	DELETE,
	DISCARD_CHANGES,
	CLOSE_RECORD,
	CANCEL_EDIT,
	SHOW_NOTES,
	SHOW_AUDIT_DETAILS,
	PRE_SAVE,
	POST_SAVE,
}

enum SlideoutPosition {
	LEFT = 'left',
	RIGHT = 'right',
}

@autoinject
@customElement('filter-slideout')
export class FilterSlideout extends BaseViewModel {
	@bindable private readonly SLIDEOUT_TYPE = SlideoutType;
	public static readonly EVENT_TYPE = SlideoutEvents;

	/**
	 * Slideout type
	 */
	@bindable private slideoutType: SlideoutType = SlideoutType.DEFAULT;
	/**
	 * If slideout is shown or not. `shown` class is added when this is `true`
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
		changeHandler: 'shownChanged',
	})
	protected shown: boolean = false;
	/**
	 * This can only be set as `left` or `right`, otherwise the slideout will not render.
	 * Default: `right`
	 */
	@bindable protected position: SlideoutPosition = SlideoutPosition.RIGHT;
	/**
	 * Hides other slideout if this is about to be shown
	 */
	@bindable protected hideHeader: boolean = false;
	/**
	 * Hides other slideout if this is about to be shown
	 */
	@bindable protected hideOthers: boolean = true;
	/**
	 * Slideout title
	 */
	@bindable protected headerTitle: string = '';
	/**
	 * Slideout subtitle
	 */
	@bindable protected headerSubtitle: string = '';
	/**
	 * Slideout id
	 */
	@bindable protected slideoutId: string;

	// uhh
	protected functionName: string;

	private psBody: PerfectScrollbar;

	private readonly refBody: HTMLElement;

	constructor(
		public readonly element: Element,
		@newInstance()
		protected readonly validationController: ValidationController,
	) {
		super();
	}

	private attached(): void {
		if (!this.refBody) {
			throw new Error(
				'Unexpected error. Missing element: i-slideout__body.',
			);
		}
		this.psBody = new PerfectScrollbar(this.refBody);
	}

	private detached(): void {
		if (this.psBody) {
			this.psBody.destroy();
			this.psBody = undefined;
		}
	}

	/**
	 * Toggle open/close slideout
	 * @returns {boolean} Is the slideout open?
	 */
	public toggle(): boolean {
		this.shown = !this.shown;
		return this.shown;
	}

	/**
	 * Open the slideout
	 */
	public open() {
		this.shown = true;
	}

	/**
	 * Close the slideout
	 */
	public close() {
		this.shown = false;
	}

	/**
	 * Is it opened?
	 */
	public isShown() {
		return this.shown;
	}

	/**
	 * Change handler for `shown`
	 * TODO: Make this do stuff. Maybe calling a callback that can be binded (see action.call in Button)?
	 */
	private shownChanged() {
		Logger.log(this.shown ? 'Showing' : 'Hiding', this.slideoutId || '');
	}

	// ---------------------------------------------------------------------------------------------
	// Static methods
	// ---------------------------------------------------------------------------------------------

	/**
	 * Gets the Slideout element given an ID
	 */
	public static getSlideoutElementById = (id: string): HTMLElement =>
		document.querySelector(`a-slideout[data-slidout-id="${id}"]`);
}
